import { Autocomplete, Box, CircularProgress, TextField } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { ThirdPartyIntegrationService } from '../../../service/ThirdPartyIntegrationService';
import type { FieldDefinition, FieldOption } from '../types';

function normalizeOptions(data: unknown): FieldOption[] {
  if (Array.isArray(data)) return data as FieldOption[];
  if (data && typeof data === 'object' && 'label' in data && 'value' in data) {
    return [data as FieldOption];
  }
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    if (Array.isArray(o.options)) return o.options as FieldOption[];
    if (Array.isArray(o.items)) return o.items as FieldOption[];
  }
  return [];
}

export interface RemoteSearchDynamicSelectProps {
  field: FieldDefinition;
  value: unknown;
  onChange: (name: string, value: unknown) => void;
  onFieldFocus?: (name: string) => void;
  onFieldBlur?: () => void;
  provider: string;
  context?: Record<string, unknown>;
  integrationId?: string;
  connectedByUserId?: string;
  disabled?: boolean;
  connected?: boolean;
  hasError: boolean;
  isDark: boolean;
}

const RemoteSearchDynamicSelect: React.FC<RemoteSearchDynamicSelectProps> = ({
  field,
  value,
  onChange,
  onFieldFocus,
  onFieldBlur,
  provider,
  context,
  integrationId,
  connectedByUserId,
  disabled,
  connected,
  hasError,
  isDark,
}) => {
  const strVal = String(value ?? '');
  const isRequired = field.validation?.required === true;

  const [options, setOptions] = useState<FieldOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(strVal);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    setInputValue(strVal);
  }, [strVal]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(inputValue), 300);
    return () => window.clearTimeout(t);
  }, [inputValue]);

  const requestContext = useMemo(() => {
    const o: Record<string, unknown> = {
      ...(context ?? {}),
      search: debouncedSearch,
    };
    if (integrationId) {
      o.integrationId = integrationId;
    } else if (connectedByUserId) {
      o.connectedByUserId = connectedByUserId;
    }
    return o;
  }, [context, debouncedSearch, integrationId, connectedByUserId]);

  const contextStableKey = useMemo(() => JSON.stringify(requestContext), [requestContext]);

  useEffect(() => {
    if (!connected) return;
    let cancelled = false;
    setLoading(true);
    const run = async () => {
      try {
        const res = await ThirdPartyIntegrationService.getDynamicOptions(
          provider,
          field.name,
          requestContext,
        );
        if (!cancelled) {
          setOptions(res?.data !== undefined ? normalizeOptions(res.data) : []);
        }
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [connected, provider, field.name, contextStableKey]);

  const inputSx = {
    width: '100%',
    maxWidth: '360px',
    '& .MuiOutlinedInput-root': {
      bgcolor: isDark ? '#101828' : '#F9FAFB',
      '& fieldset': { borderColor: isDark ? '#475467' : '#D0D5DD' },
    },
  };

  const errorOutline = hasError
    ? {
        '& .MuiOutlinedInput-notchedOutline': {
          border: `1px solid ${isDark ? '#F59E0B' : '#EAB308'}`,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          border: `1px solid ${isDark ? '#F59E0B' : '#EAB308'}`,
        },
      }
    : {};

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const selectedOption = options.find(o => o.value === strVal) ?? null;

  return (
    <Autocomplete<FieldOption, false, false, true>
      className="nodrag"
      freeSolo
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      filterOptions={opts => opts}
      options={options}
      loading={loading}
      disabled={disabled || !connected}
      disableClearable={isRequired}
      value={selectedOption}
      inputValue={inputValue}
      onInputChange={(_, newInput, reason) => {
        setInputValue(newInput);
        if (reason === 'clear' && !isRequired) {
          onChange(field.name, '');
        }
      }}
      onChange={(_, newVal) => {
        if (newVal == null) {
          if (!isRequired) {
            onChange(field.name, '');
            setInputValue('');
          }
          return;
        }
        if (typeof newVal === 'string') {
          onChange(field.name, newVal);
          setInputValue(newVal);
          return;
        }
        onChange(field.name, newVal.value);
        setInputValue(newVal.value);
      }}
      getOptionLabel={opt => (typeof opt === 'string' ? opt : opt.label)}
      isOptionEqualToValue={(opt, val) => {
        if (typeof val === 'string') return opt.value === val;
        return opt.value === val.value;
      }}
      renderInput={params => (
        <TextField
          {...params}
          placeholder={field.placeholder ?? `Search ${field.label}`}
          size="small"
          onFocus={() => onFieldFocus?.(field.name)}
          onBlur={() => {
            onFieldBlur?.();
            const trimmed = inputValue.trim();
            const match = options.find(
              o => o.value === trimmed || o.label === trimmed,
            );
            if (match) {
              onChange(field.name, match.value);
              setInputValue(match.value);
            } else {
              onChange(field.name, trimmed);
            }
          }}
          error={hasError}
          helperText={hasError ? 'Required' : undefined}
          sx={{ ...inputSx, ...errorOutline }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', mr: 0.5 }}>
                    <CircularProgress color="inherit" size={18} />
                  </Box>
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default RemoteSearchDynamicSelect;
